import FileService from "#src/components/Services/FileService";
import { useAnnotationStore } from "#src/stores/annotationStore";
import { FormSubmissionType } from "@validereinc/domain";
import { invariant } from "@validereinc/utilities";
import { Canvas, FabricObjectProps, Group, Point, Rect, XY } from "fabric";
import { useEffect } from "react";

export type directionType = "left" | "right";
export type measurementsType = "sediment" | "water" | "interface";
export type ValueType = number | "error" | undefined;
export type backgroundType = "regular" | "uv";
export type AggregateFunctionType = "minimum" | "maximum" | "average";

export const measurementKeys: measurementsType[] = [
  "interface",
  "water",
  "sediment",
];

export const annotationToolConfig = {
  highDetailedBezierCurveSampling: false,
  tickLineThickness: 1,
  aggregateLineThickness: 1,
  dynamicColoursEnabled: false,
  defaultTickMarkerColour: "#ff4040",
  maxCanvasHeight: 800,
};

export const annotationToolObjectIDs = {
  AGGREGATE_LINE: "aggregate-line",
  AGGREGATE_LINE_LABEL: "aggregate-line-label",
  DRAWN_PATH: "drawn-path",
  TOP_TICK_GROUP: "top-tick-group",
  BOTTOM_TICK_GROUP: "bottom-tick-group",
  HALF_IMAGE_BLOCKER_RECTANGLE: "half-image-blocker-rectangle",
};

export const findObjectById = (fabricCanvas: Canvas, id: string) => {
  return fabricCanvas.getObjects().find((obj) => obj.get("id") === id);
};

export const findObjectsByIds = (fabricCanvas: Canvas, ids: string[]) => {
  return fabricCanvas
    .getObjects()
    .filter((obj) => ids.includes(obj.get("id") as string));
};

export const addHalfImageBlockerRectangle = (
  fabricCanvas?: Canvas,
  focusedHalf?: "left" | "right"
) => {
  if (!fabricCanvas) return;
  const halfImageBlockerRectangle = fabricCanvas
    .getObjects()
    .find(
      (obj) =>
        obj.get("id") === annotationToolObjectIDs.HALF_IMAGE_BLOCKER_RECTANGLE
    ) as Rect;
  if (halfImageBlockerRectangle) {
    fabricCanvas.remove(halfImageBlockerRectangle);
  }
  if (focusedHalf) {
    fabricCanvas.add(
      new Rect({
        left: focusedHalf === "right" ? 0 : fabricCanvas.width / 2,
        top: 0,
        /** Fabric 7 defaults origin to center; use left/top so rect covers the intended half. */
        originX: "left",
        originY: "top",
        width: fabricCanvas.width / 2,
        height: fabricCanvas.height,
        fill: "rgba(0, 0, 0, 0.7)",
        id: annotationToolObjectIDs.HALF_IMAGE_BLOCKER_RECTANGLE,
        ...previewOnlyAttributes,
      })
    );
  }
};

// Helper function to get contrasting color based on background
const getContrastingColor = (canvas: Canvas, x: number, y: number): string => {
  try {
    // Get pixel data at the specified position
    const imageData = canvas.getContext().getImageData(x, y, 1, 1);
    const [r, g, b] = imageData.data;

    // Calculate luminance to determine if background is light or dark
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return high contrast color
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  } catch (error) {
    // Fallback to black if sampling fails
    return "#000000";
  }
};

// Helper function to sample multiple points and get best contrast
const getBestContrastColor = (
  canvas: Canvas,
  points: Array<{ x: number; y: number }>
): string => {
  const colors = points.map((point) =>
    getContrastingColor(canvas, point.x, point.y)
  );

  // Count occurrences of each color
  const colorCounts = colors.reduce(
    (acc, color) => {
      acc[color] = (acc[color] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  // Return the most frequent contrasting color
  return Object.keys(colorCounts).reduce((a, b) =>
    colorCounts[a] > colorCounts[b] ? a : b
  );
};

export const updateDynamicColors = ({
  groupId,
  canvas,
}: {
  groupId: string;
  canvas: Canvas;
}) => {
  if (canvas) {
    const tickGroup = canvas
      .getObjects()
      .find((obj) => obj.get("id") === groupId) as Group;

    if (tickGroup) {
      const groupBounds = tickGroup.getBoundingRect();
      let y = groupBounds.top;

      if (groupId === "bottom-tick-group") {
        y += groupBounds.height;
      }
      const samplePoints = [
        { x: groupBounds.left, y },
        {
          x: groupBounds.left + groupBounds.width * 0.25,
          y,
        },
        {
          x: groupBounds.left + groupBounds.width * 0.5,
          y,
        },
        {
          x: groupBounds.left + groupBounds.width * 0.75,
          y,
        },
        {
          x: groupBounds.left + groupBounds.width,
          y,
        },
      ];

      const contrastColor = getBestContrastColor(canvas, samplePoints);

      tickGroup
        .getObjects()
        .find((obj) => obj.type === "line")
        ?.set("stroke", contrastColor);

      tickGroup
        .getObjects()
        .find((obj) => obj.type === "text")
        ?.set("fill", contrastColor);

      canvas.renderAll();
    }
  }
};

// Round to half-pixel values (0.5 increments)
const pixelRounder = (point: XY): XY => {
  return {
    x: Math.round(point.x * 2) / 2,
    y: Math.round(point.y * 2) / 2,
  };
};

// Helper function to add a point only if it's different from the last one
export const addUniquePixelPoint = (points: XY[], newPoint: XY) => {
  const pixelPoint = pixelRounder(newPoint);

  const lastPoint = points[points.length - 1];
  if (
    !lastPoint ||
    pixelPoint.x !== lastPoint.x ||
    pixelPoint.y !== lastPoint.y
  ) {
    points.push(pixelPoint);
  }
};

// Quadratic Bézier formula: B(t) = (1-t)²P₀ + 2(1-t)tP₁ + t²P₂
const quadraticCurveFormula = (
  startValue: number,
  controlValue: number,
  endValue: number,
  t: number
) =>
  (1 - t) * (1 - t) * startValue +
  2 * (1 - t) * t * controlValue +
  t * t * endValue;

// Helper function to sample points along a quadratic curve with dynamic sampling
export const sampleQuadraticCurve = (
  startPoint: XY,
  controlPoint: XY,
  endPoint: XY
) => {
  // Calculate the approximate length of the curve to determine sample count
  // We use the distance from start->control + control->end as an approximation
  const controlDistance = Math.sqrt(
    Math.pow(controlPoint.x - startPoint.x, 2) +
      Math.pow(controlPoint.y - startPoint.y, 2)
  );
  const endDistance = Math.sqrt(
    Math.pow(endPoint.x - controlPoint.x, 2) +
      Math.pow(endPoint.y - controlPoint.y, 2)
  );
  const approximateLength = controlDistance + endDistance;

  // Sample at least every 0.5 pixels (since we're using half-pixel precision)
  // Multiply by 2 to ensure we don't miss any half-pixel positions
  const samples = Math.max(10, Math.ceil(approximateLength * 2));

  const points: XY[] = [];
  let lastPixelPoint: XY | null = null;

  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const x = quadraticCurveFormula(
      startPoint.x,
      controlPoint.x,
      endPoint.x,
      t
    );

    const y = quadraticCurveFormula(
      startPoint.y,
      controlPoint.y,
      endPoint.y,
      t
    );

    // Round to half-pixel values (0.5 increments)
    const pixelPoint: XY = pixelRounder({ x, y });

    // Only add if different from previous pixel point
    if (
      !lastPixelPoint ||
      pixelPoint.x !== lastPixelPoint.x ||
      pixelPoint.y !== lastPixelPoint.y
    ) {
      points.push(pixelPoint);
      lastPixelPoint = pixelPoint;
    }
  }

  return points;
};

export const previewOnlyAttributes: Partial<FabricObjectProps> = {
  hoverCursor: "default",
  moveCursor: "default",
  selectable: false,
  evented: false,
  hasControls: false,
  hasBorders: false,
  lockMovementX: true,
  lockMovementY: true,
  lockRotation: true,
  lockScalingX: true,
  lockScalingY: true,
  lockSkewingX: true,
  lockSkewingY: true,
};

export const aggregateFunctions: Record<
  AggregateFunctionType,
  (points: XY[]) => number
> = {
  // The logic of min and max are swapped. "Minimum" value means the highest y value, and "Maximum" value means the lowest y value.
  minimum: (points) => {
    if (points.length === 0) return 0;
    return Math.max(...points.map((point) => point.y));
  },
  maximum: (points) => {
    if (points.length === 0) return 0;
    return Math.min(...points.map((point) => point.y));
  },
  average: (points) => {
    if (points.length === 0) return 0;
    return points.reduce((sum, p) => sum + p.y, 0) / points.length;
  },
};

export const resetView = (fabricCanvas: Canvas) => {
  if (!fabricCanvas) return;
  fabricCanvas.setZoom(1);
  fabricCanvas.absolutePan(new Point(0, 0));
};

export const downloadTubeImage = async (fileId?: string) => {
  if (!fileId) return;
  const data = await FileService.getFileDownloadUrl(fileId);

  if (!data) {
    return;
  }

  const {
    data: {
      data: [fileData],
    },
  } = data;

  invariant(fileData, "Could not extract file data");
  invariant(
    fileData.file_scan_status === "safe",
    "The file download link is not available."
  );

  // IMPROVE: assertion is a stop-gap until we get time to move the FileService to the domain package and define types there
  const { uri: downloadUrl } = fileData as {
    file_id: string;
    file_scan_status: "safe" | "unsafe";
    uri: string;
  };

  invariant(downloadUrl, "The file download URL does not exist.");

  // IMPROVE: assertion is a stop-gap until we get time to move the FileService to the domain package and define types there
  return fileData as {
    file_id: string;
    file_scan_status: "safe" | "unsafe";
    uri: string;
  };
};

export const useHydrateStoreFromInitialValues = (
  initialValues: Record<string, string>
) => {
  const { setImageURLs, setCorrectionValues } = useAnnotationStore();

  useEffect(() => {
    if (!initialValues) return;

    const parallaxCorrection = initialValues[
      "$.questions.parallaxCorrection.default_answer"
    ] as unknown as number;

    const dilutionCorrection = initialValues[
      "$.questions.dilutionCorrection.default_answer"
    ] as unknown as number;

    const regularRef = (
      initialValues[
        "$.questions.tubeNormalImage.default_answer"
      ] as unknown as { ref: string }
    )?.ref;

    const uvRef = (
      initialValues["$.questions.tubeUVImage.default_answer"] as unknown as {
        ref: string;
      }
    )?.ref;

    if (regularRef && uvRef) {
      Promise.all([
        downloadTubeImage(regularRef),
        downloadTubeImage(uvRef),
      ]).then(([regular, uv]) => {
        if (regular?.uri && uv?.uri) {
          setImageURLs({ uv: uv.uri, regular: regular.uri });
        }
      });
    }

    if (parallaxCorrection && dilutionCorrection) {
      setCorrectionValues({ dilutionCorrection, parallaxCorrection });
    }
  }, [initialValues]);
};

export const usePrepareFormSubmissionAnswers = ({
  annotationFormSchemaId,
  defaultValues,
  taskId,
}: {
  annotationFormSchemaId: string;
  defaultValues: Record<string, any>;
  taskId: string;
}) => {
  const {
    measurements,
    measurementData,
    dilutionCorrection,
    parallaxCorrection,
    resizeRatio,
    uploadedScreenshotRefs,
  } = useAnnotationStore();

  /**
   * Sanitizes measurement values for form submission with cascading fallback logic.
   * When a value is explicitly set to 0:
   * - Sediment: stays 0
   * - Water: falls back to sediment's value
   * - Interface: falls back to water's value (which may cascade to sediment)
   */
  const sanitizeValue = (
    direction: directionType,
    measurement: measurementsType
  ) => {
    const currentMeasurementValue = measurements[`${direction}_${measurement}`];

    // If error, return empty string
    if (currentMeasurementValue === "error") {
      return "";
    }

    // If undefined, return 0
    if (currentMeasurementValue === undefined) {
      return 0;
    }

    // If value is explicitly 0, apply cascading fallback logic
    if (currentMeasurementValue === 0) {
      // Sediment stays 0
      if (measurement === "sediment") {
        return 0;
      }

      // Water falls back to sediment
      if (measurement === "water") {
        const sedimentValue = measurements[`${direction}_sediment`];
        return sedimentValue === "error" || sedimentValue === undefined
          ? 0
          : sedimentValue;
      }

      // Interface falls back to water (which may cascade to sediment)
      if (measurement === "interface") {
        const waterValue = measurements[`${direction}_water`];
        // If water is also 0 or undefined, get sediment instead
        if (waterValue === 0 || waterValue === undefined) {
          const sedimentValue = measurements[`${direction}_sediment`];
          return sedimentValue === "error" || sedimentValue === undefined
            ? 0
            : sedimentValue;
        }
        return waterValue === "error" ? 0 : waterValue;
      }
    }

    return currentMeasurementValue;
  };

  const tubeMeasurement = (
    direction: directionType,
    measurement: measurementsType
  ) => {
    const currentMeasurementData =
      measurementData[`${direction}_${measurement}`];

    const currentMeasurementValue = measurements[`${direction}_${measurement}`];

    const resizeFactor = 1 / (resizeRatio ?? 1);

    return {
      tickMarkUpper_X: {
        value: ["10"],
      },
      tickMarkLower_X: {
        value: ["10"],
      },
      tickMarkUpper_Y: {
        value: [`${(currentMeasurementData?.topTickY ?? 0) * resizeFactor}`],
      },
      tickMarkLower_Y: {
        value: [`${(currentMeasurementData?.bottomTickY ?? 0) * resizeFactor}`],
      },
      meniscus_X: {
        value: (currentMeasurementData?.measurementPoints ?? [])
          .map((point) => point.x * resizeFactor)
          .map((value) => value.toString()),
      },
      meniscus_Y: {
        value: (currentMeasurementData?.measurementPoints ?? [])
          .map((point) => point.y * resizeFactor)
          .map((value) => value.toString()),
      },
      tickMarkUpper_value: {
        value: currentMeasurementData?.topTickValue.toString() ?? "",
      },
      tickMarkLower_value: {
        value: currentMeasurementData?.bottomTickValue.toString() ?? "",
      },
      meniscus_cp: {
        value: aggregateFunctions[
          currentMeasurementData?.aggregateFunction ?? "average"
        ](
          (currentMeasurementData?.measurementPoints ?? []).map(
            (point) =>
              ({
                x: point.x * resizeFactor,
                y: point.y * resizeFactor,
              }) as Point
          )
        ),
      },
      measurementError: {
        value: currentMeasurementValue === "error",
      },
    };
  };

  const prepareFormSubmissionAnswers: () =>
    | Partial<FormSubmissionType>
    | undefined = () => {
    if (!parallaxCorrection || !dilutionCorrection || !resizeRatio) return;

    if (
      !uploadedScreenshotRefs.left_uv ||
      !uploadedScreenshotRefs.right_uv ||
      !uploadedScreenshotRefs.left_regular ||
      !uploadedScreenshotRefs.right_regular
    )
      return;

    return {
      answers: {
        correction_factor: [
          {
            parallaxCorrection: {
              value: parallaxCorrection,
            },
            dilutionCorrection: {
              value: dilutionCorrection,
            },
          },
        ],
        ctr_annotated_tube_images: [
          {
            LeftNormalCTRImage: {
              value: uploadedScreenshotRefs.left_regular,
            },
            RightNormalCTRImage: {
              value: uploadedScreenshotRefs.right_regular,
            },
            LeftUVCTRImage: {
              value: uploadedScreenshotRefs.left_uv,
            },
            RightUVCTRImage: {
              value: uploadedScreenshotRefs.right_uv,
            },
            tubeNormalImage: {
              value:
                defaultValues["$.questions.tubeNormalImage.default_answer"],
            },
            tubeUVImage: {
              value: defaultValues["$.questions.tubeUVImage.default_answer"],
            },
          },
        ],
        left_tube_measurement_sediment: [
          {
            ...tubeMeasurement("left", "sediment"),
            meniscus_value_sediment: {
              value: sanitizeValue("left", "sediment"),
            },
          },
        ],
        right_tube_measurement_sediment: [
          {
            ...tubeMeasurement("right", "sediment"),
            meniscus_value_sediment: {
              value: sanitizeValue("right", "sediment"),
            },
          },
        ],

        left_tube_measurement_water_sediment: [
          {
            ...tubeMeasurement("left", "water"),
            meniscus_value_water_sediment: {
              value: sanitizeValue("left", "water"),
            },
          },
        ],

        right_tube_measurement_water_sediment: [
          {
            ...tubeMeasurement("right", "water"),
            meniscus_value_water_sediment: {
              value: sanitizeValue("right", "water"),
            },
          },
        ],

        left_tube_measurement_water_sediment_interface: [
          {
            ...tubeMeasurement("left", "interface"),
            meniscus_value_water_sediment_interface: {
              value: sanitizeValue("left", "interface"),
            },
          },
        ],

        right_tube_measurement_water_sediment_interface: [
          {
            ...tubeMeasurement("right", "interface"),
            meniscus_value_water_sediment_interface: {
              value: sanitizeValue("right", "interface"),
            },
          },
        ],
      },
      form_schema_id: annotationFormSchemaId,
      workflow_task_id: taskId,
    };
  };

  return { prepareFormSubmissionAnswers };
};

export const createPathFromPoints = (points: XY[]): string => {
  if (points.length === 0) return "";

  let pathData = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length; i++) {
    pathData += ` L ${points[i].x} ${points[i].y}`;
  }

  return pathData;
};

export const wait = (amount = 0) =>
  new Promise((resolve) => setTimeout(resolve, amount));
